/* eslint-disable jsx-a11y/anchor-is-valid */
import React, {SyntheticEvent, useEffect, useRef, useState} from 'react'
import ApexCharts, {ApexOptions} from 'apexcharts'
import {getCSS, getCSSVariableValue} from '../../../assets/ts/_utils'
import {StatsModel} from '../../../../app/pages/dashboard/models/StatsModel'
import {Stats} from 'fs'

type Props = {
  className: string
  stats: StatsModel[]
}

const ChartsWidget3: React.FC<Props> = ({className, stats}) => {
  const chartRef = useRef<HTMLDivElement | null>(null)

  const [chartAxisType, setChartAxisType] = useState('Month')

  const [chartData, setChartData] = useState(stats)

  useEffect(() => {
    setChartAxisType('Month')
    setChartData(stats)
  }, [stats])

  useEffect(() => {
    if (!chartRef.current) {
      return
    }

    const height = parseInt(getCSS(chartRef.current, 'height'))

    const chart = new ApexCharts(chartRef.current, getChartOptions(height, chartData))
    if (chart) {
      chart.render()
    }

    return () => {
      if (chart) {
        chart.destroy()
      }
    }
  }, [chartRef, chartData])

  function changeChartAxis(type: string) {
    setChartAxisType(type)
    if (type === 'Month') {
      setChartData(stats)
    }
    if (type == 'Week') {
      setChartData(stats.slice(-7))
    }
  }

  return (
    <div className={`card ${className}`}>
      {/* begin::Header */}
      <div className='card-header border-0 pt-5'>
        <h3 className='card-title align-items-start flex-column'>
          <span className='card-label fw-bolder fs-3 mb-1'>Recent Visitors</span>
        </h3>

        {/* begin::Toolbar */}
        <div className='card-toolbar' data-kt-buttons='true'>
          <a
            onClick={() => changeChartAxis('Week')}
            className={`btn btn-sm btn-color-muted btn-active btn-active-primary ${
              chartAxisType === 'Week' ? 'active' : ''
            } px-4 me-1`}
            id='kt_charts_widget_3_year_btn'
          >
            Week
          </a>

          <a
            onClick={() => changeChartAxis('Month')}
            className={`btn btn-sm btn-color-muted btn-active ${
              chartAxisType === 'Month' ? 'active' : ''
            } btn-active-primary px-4 me-1`}
            id='kt_charts_widget_3_month_btn'
          >
            Month
          </a>
        </div>
        {/* end::Toolbar */}
      </div>
      {/* end::Header */}

      {/* begin::Body */}
      <div className='card-body'>
        {/* begin::Chart */}
        <div ref={chartRef} id='kt_charts_widget_3_chart' style={{height: '350px'}}></div>
        {/* end::Chart */}
      </div>
      {/* end::Body */}
    </div>
  )
}

export {ChartsWidget3}

function getChartOptions(height: number, stats: StatsModel[]): ApexOptions {
  const labelColor = getCSSVariableValue('--bs-gray-500')
  const borderColor = getCSSVariableValue('--bs-gray-200')
  const baseColor = getCSSVariableValue('--bs-info')
  const lightColor = getCSSVariableValue('--bs-light-info')

  return {
    series: [
      {
        name: 'Leads',
        data: stats.map((stat) => stat.count),
      },
    ],
    chart: {
      fontFamily: 'inherit',
      type: 'area',
      height: 350,
      toolbar: {
        show: false,
      },
    },
    plotOptions: {},
    legend: {
      show: false,
    },
    dataLabels: {
      enabled: false,
    },
    fill: {
      type: 'solid',
      opacity: 1,
    },
    stroke: {
      curve: 'smooth',
      show: true,
      width: 3,
      colors: [baseColor],
    },
    xaxis: {
      categories: stats.map((stat) => `${stat.day}-${stat.month}-${stat.year}`),
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
      labels: {
        style: {
          colors: labelColor,
          fontSize: '12px',
        },
      },
      crosshairs: {
        position: 'front',
        stroke: {
          color: baseColor,
          width: 1,
          dashArray: 3,
        },
      },
      tooltip: {
        enabled: true,
        formatter: undefined,
        offsetY: 0,
        style: {
          fontSize: '12px',
        },
      },
    },
    yaxis: {
      labels: {
        style: {
          colors: labelColor,
          fontSize: '12px',
        },
      },
    },
    states: {
      normal: {
        filter: {
          type: 'none',
          value: 0,
        },
      },
      hover: {
        filter: {
          type: 'none',
          value: 0,
        },
      },
      active: {
        allowMultipleDataPointsSelection: false,
        filter: {
          type: 'none',
          value: 0,
        },
      },
    },
    tooltip: {
      style: {
        fontSize: '12px',
      },
      y: {
        formatter: function (val) {
          return val + ' leads'
        },
      },
    },
    colors: [lightColor],
    grid: {
      borderColor: borderColor,
      strokeDashArray: 4,
      yaxis: {
        lines: {
          show: true,
        },
      },
    },
    markers: {
      strokeColors: baseColor,
      strokeWidth: 3,
    },
  }
}
